import { useState, useCallback } from "react"
import {
  BaseEdge,
  getBezierPath,
  useReactFlow,
  type EdgeProps,
} from "@xyflow/react"
import { getModuleDefinition } from "@/lib/registry/modules"
import { getDataTypeColor } from "@/lib/registry/data-types"
import { getStructuredOutputHandles, type OutputSchemaField } from "@/lib/registry/text-builder"
import { usePipelineStore } from "@/lib/stores/pipeline-store"
import { useExecutionStore } from "@/lib/stores/execution-store"

export function TypedEdge({
  id,
  source,
  target,
  sourceHandleId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
}: EdgeProps) {
  const [hovered, setHovered] = useState(false)
  const { setEdges } = useReactFlow()
  const nodes = usePipelineStore((s) => s.nodes)
  const targetNodeStatus = useExecutionStore((s) => s.nodeStatuses[target])

  // Determine edge color from source output type
  const sourceNode = nodes.find((n) => n.id === source)
  const sourceDef = sourceNode
    ? getModuleDefinition(sourceNode.data.module)
    : undefined
  // For llm_text with structured output schema, handles are dynamic — check schema first
  const sourceOutput = (() => {
    if (sourceNode?.data.module === "llm_text") {
      const schema = Array.isArray(sourceNode.data.config.output_schema)
        ? (sourceNode.data.config.output_schema as OutputSchemaField[])
        : []
      if (schema.length > 0) {
        const schemaHandle = getStructuredOutputHandles(schema).find(
          (o) => o.name === sourceHandleId
        )
        if (schemaHandle) return schemaHandle
      }
    }
    return sourceDef?.outputs.find((o) => o.name === sourceHandleId)
  })()
  const strokeColor = sourceOutput
    ? getDataTypeColor(sourceOutput.type)
    : "var(--border)"

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  })

  const handleDelete = useCallback(() => {
    setEdges((edges) => edges.filter((e) => e.id !== id))
  }, [id, setEdges])

  const isFeeding = targetNodeStatus === "running"

  return (
    <>
      {/* Invisible wider path for easier hover targeting */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: isFeeding ? strokeColor : strokeColor,
          strokeWidth: isFeeding ? 3 : hovered || selected ? 3 : 2,
          opacity: isFeeding ? 1 : hovered || selected ? 1 : 0.7,
          strokeDasharray: isFeeding ? "8 4" : undefined,
          animation: isFeeding ? "edge-flow 0.6s linear infinite" : undefined,
          transition: "stroke-width 0.15s, opacity 0.15s",
        }}
      />
      {/* Delete button on hover */}
      {hovered && (
        <foreignObject
          x={labelX - 10}
          y={labelY - 10}
          width={20}
          height={20}
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <button
            type="button"
            onClick={handleDelete}
            onMouseEnter={() => setHovered(true)}
            className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white text-xs hover:bg-destructive/80 transition-colors shadow-sm"
          >
            &times;
          </button>
        </foreignObject>
      )}
    </>
  )
}
